/**
 * WebXR AR Viewer - Production UX
 * - 1 finger = drag model (position only, no scale change)
 * - 2 fingers = pinch scale + rotate
 * - Scale limits: 25% - 300%
 * - allowUserScaling prop controls snap-back behavior
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X, Move, Hand, AlertCircle, Loader2, RotateCcw, Camera, Download, Share2 } from "lucide-react";
import { getT } from "@/i18n/translations";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils";

const DEBUG = true;
const log = (...args) => { if (DEBUG) console.log("[WebXR]", ...args); };
const logError = (...args) => { console.error("[WebXR ERROR]", ...args); };

// Scale limits
const SCALE_MIN = 0.25;
const SCALE_MAX = 3.0;
const SNAP_THRESHOLD = 0.03; // Snap to 100% when within ±3%
const DRAG_THRESHOLD_PX = 8; // Min finger travel before a model drag activates

const WebXRViewer = ({
  glbUrl,
  projectName,
  onClose,
  onFallbackToSceneViewer,
  allowUserScaling = true,
  debugMode = false, // Show debug info panel
  language = "pl",
  audioUrl = null,
  audioPlaybackMode = "once",
}) => {
  const t = getT(language);
  // UI State
  const [phase, setPhase] = useState("onboarding");
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [hasHit, setHasHit] = useState(false);
  const [modelPlaced, setModelPlaced] = useState(false);
  const [noHitWarning, setNoHitWarning] = useState(false);
  const [showPlacedHint, setShowPlacedHint] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadStatus, setLoadStatus] = useState("");
  const [modelLoaded, setModelLoaded] = useState(false);
  const [currentScalePercent, setCurrentScalePercent] = useState(100);
  const [showScaleIndicator, setShowScaleIndicator] = useState(false);
  const [hitStable, setHitStable] = useState(false); // Is hit-test stable enough for placement
  const [hitDebugInfo, setHitDebugInfo] = useState({}); // Debug info for hit-test
  const [cameraAccessAvailable, setCameraAccessAvailable] = useState(false); // WebXR raw camera access
  const [photoUrl, setPhotoUrl] = useState(null); // AR photo preview (object URL)

  // Three.js refs
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const reticleRef = useRef(null);
  const modelTemplateRef = useRef(null);
  const placedModelRef = useRef(null);
  const sessionRef = useRef(null);
  const dracoLoaderRef = useRef(null);
  
  // Animation refs
  const animationMixerRef = useRef(null);
  const animationClipsRef = useRef([]);
  const animationActionsRef = useRef([]);
  const lastAnimationTimeRef = useRef(0);

  // Audio - own instance, tied to model placement (Android WebXR)
  const audioElRef = useRef(null);

  // Hit test sources
  const viewerHitTestSourceRef = useRef(null);
  const transientHitTestSourceRef = useRef(null);
  const localSpaceRef = useRef(null);

  // State refs for XR callbacks (must be refs to avoid stale closures)
  const stateRef = useRef({
    hasHit: false,
    modelPlaced: false,
    reticleVisible: false,
    modelRotation: 0,
    modelScale: 1.0, // Base scale (1.0 = 100%)
    baseModelScale: new THREE.Vector3(1, 1, 1), // Original scale from loaded model
  });

  // Touch gesture tracking
  const touchRef = useRef({
    count: 0,
    lastAngle: null,
    lastPinchDistance: null,
    isGesturing: false,
    gestureType: null, // 'rotate' | 'scale' | 'both'
  });

  // Timers
  const noHitTimerRef = useRef(null);
  const placedHintTimerRef = useRef(null);
  const scaleIndicatorTimerRef = useRef(null);

  // Hit-test stabilization - improved algorithm
  const hitHistoryRef = useRef([]); // Last N hit positions {x, y, z, timestamp}
  const stableHitRef = useRef(null); // Stabilized hit position
  const diagLogTsRef = useRef(0); // throttle for [XR HIT]/[RETICLE] diag logs
  const lockedYRef = useRef(null); // Locked Y after placement
  const hitStabilityRef = useRef({ isStable: false, stableSince: null }); // Stability tracking

  // AR photo capture (WebXR raw camera access)
  const captureRequestedRef = useRef(false);
  const captureRtRef = useRef(null);
  const captureBgRef = useRef(null);
  const captureCamRef = useRef(null);
  const photoBlobRef = useRef(null);

  // Post-placement drag control (raycast-gated so empty taps never move model)
  const dragActiveRef = useRef(false);
  const pendingDragRef = useRef(null); // { startX, startY } armed on a model-hit touchstart
  const raycasterRef = useRef(new THREE.Raycaster());
  const pointerNdcRef = useRef(new THREE.Vector2());
  
  // Configuration for hit-test stabilization
  const HIT_HISTORY_SIZE = 20; // Number of frames to collect
  const HIT_Y_THRESHOLD = 0.08; // Max Y deviation from median (8cm)
  const HIT_XZ_THRESHOLD = 0.15; // Max XZ deviation from median (15cm)
  const STABILITY_TIME_MS = 400; // Time required for stable placement (400ms)
  const OUTLIER_REJECTION = 0.2; // Reject top/bottom 20% of Y values

  // --- Audio (placement-synced, own instance) ---
  useEffect(() => {
    if (!audioUrl) return undefined;
    const el = new Audio(audioUrl);
    el.loop = audioPlaybackMode === "loop";
    el.preload = "auto";
    audioElRef.current = el;
    return () => {
      try {
        el.pause();
        el.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
      audioElRef.current = null;
    };
  }, [audioUrl, audioPlaybackMode]);

  // Unlock audio within a user gesture (Chrome Android autoplay policy)
  const unlockAudio = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      el.muted = true;
      const p = el.play();
      if (p && p.then) {
        p.then(() => {
          el.pause();
          el.currentTime = 0;
          el.muted = false;
        }).catch(() => {
          el.muted = false;
        });
      } else {
        el.pause();
        el.currentTime = 0;
        el.muted = false;
      }
    } catch (e) {
      el.muted = false;
    }
  }, []);

  // Start audio from the beginning (on model placement)
  const startAudio = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      el.muted = false;
      el.currentTime = 0;
      el.play().catch(() => {});
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Stop + reset audio (reset placement / exit / cleanup)
  const stopAudio = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      el.pause();
      el.currentTime = 0;
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Initialize loaders
  const initLoaders = useCallback(() => {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");
    dracoLoader.setDecoderConfig({ type: "js" });
    dracoLoaderRef.current = dracoLoader;
    log("DRACO loader initialized");
    return dracoLoader;
  }, []);

  // Load GLB model
  const loadModel = useCallback(async () => {
    return new Promise((resolve, reject) => {
      setLoadStatus("Inicjalizacja...");
      log("Model URL:", glbUrl);

      const loader = new GLTFLoader();
      if (dracoLoaderRef.current) {
        loader.setDRACOLoader(dracoLoaderRef.current);
      }

      let lastProgress = 0;
      let lastProgressTime = Date.now();
      const progressCheck = setInterval(() => {
        if (Date.now() - lastProgressTime > 30000 && lastProgress < 100) {
          clearInterval(progressCheck);
          reject(new Error(`Ładowanie zatrzymane na ${lastProgress}%`));
        }
      }, 5000);

      setLoadStatus("Pobieranie modelu...");

      loader.load(
        glbUrl,
        (gltf) => {
          clearInterval(progressCheck);
          setLoadStatus("Przetwarzanie...");
          try {
            const model = gltf.scene;
            
            // Get original bounding box for diagnostics
            const box = new THREE.Box3().setFromObject(model);
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            
            // Log original model dimensions (for debugging)
            log("Model loaded - ORIGINAL dimensions:", {
              size: { x: size.x.toFixed(3), y: size.y.toFixed(3), z: size.z.toFixed(3) },
              maxDim: Math.max(size.x, size.y, size.z).toFixed(3),
              originalScale: model.scale.clone()
            });

            // ============================================
            // [WEBXR][GLB] Device-compatibility diagnostics
            // ============================================
            try {
              let skinnedCount = 0;
              let maxBones = 0;
              let morphCount = 0;
              let maxTex = 0;
              const matFeatures = new Set();
              model.traverse((o) => {
                if (o.isSkinnedMesh) {
                  skinnedCount += 1;
                  const bones = o.skeleton?.bones?.length || 0;
                  if (bones > maxBones) maxBones = bones;
                }
                if (o.morphTargetInfluences && o.morphTargetInfluences.length) {
                  morphCount = Math.max(morphCount, o.morphTargetInfluences.length);
                }
                if (o.material) {
                  const mats = Array.isArray(o.material) ? o.material : [o.material];
                  mats.forEach((m) => {
                    if (m.transmission) matFeatures.add("transmission");
                    if (m.clearcoat) matFeatures.add("clearcoat");
                    if (m.normalMap) matFeatures.add("normalMap");
                    if (m.transparent) matFeatures.add("transparent");
                    if (m.side === THREE.DoubleSide) matFeatures.add("doubleSide");
                    if (m.emissive && (m.emissiveMap || m.emissiveIntensity > 0)) matFeatures.add("emissive");
                    ["map", "normalMap", "roughnessMap", "metalnessMap", "emissiveMap"].forEach((k) => {
                      const img = m[k]?.image;
                      if (img && img.width) maxTex = Math.max(maxTex, img.width, img.height);
                    });
                  });
                }
              });
              console.info("[WEBXR][GLB] skinnedMeshes=%d maxBones=%d morphTargets=%d maxTextureInModel=%d materials=%s",
                skinnedCount, maxBones, morphCount, maxTex, Array.from(matFeatures).join(",") || "basic");
            } catch (diagErr) {
              console.warn("[WEBXR][GLB] diagnostics error:", diagErr);
            }
            // ============================================
            
            // ============================================
            // ANIMATION DETECTION AND SETUP
            // ============================================
            const animations = gltf.animations;
            if (animations && animations.length > 0) {
              log("🎬 [ANIMATION] Detected animation clips:", animations.length);
              animations.forEach((clip, index) => {
                log(`  Clip ${index}: "${clip.name}", duration: ${clip.duration.toFixed(2)}s, tracks: ${clip.tracks.length}`);
              });
              
              // Store animations for later use when model is placed
              animationClipsRef.current = animations;
              stateRef.current.hasAnimations = true;
            } else {
              log("🎬 [ANIMATION] No animation clips found in model");
              animationClipsRef.current = [];
              stateRef.current.hasAnimations = false;
            }
            // ============================================
            
            // DO NOT normalize scale! Keep original scale from file (1:1 with Scene Viewer)
            // model.scale stays at (1, 1, 1) - the scale from the GLB file
            
            // Store base scale as 1.0 (original file scale)
            stateRef.current.baseModelScale = new THREE.Vector3(1, 1, 1);
            
            // Store original dimensions for debug display
            stateRef.current.originalSize = size.clone();
            stateRef.current.originalMaxDim = Math.max(size.x, size.y, size.z);
            
            // Center model horizontally, place on ground
            model.position.x = -center.x;
            model.position.z = -center.z;
            model.position.y = -box.min.y; // Place bottom of model at y=0
            
            log("Model prepared - scale:", model.scale, "position:", model.position);
            
            modelTemplateRef.current = model;
            setLoadProgress(100);
            setModelLoaded(true);
            resolve(model);
          } catch (e) {
            reject(new Error(`Błąd przetwarzania: ${e.message}`));
          }
        },
        (progress) => {
          if (progress.lengthComputable) {
            const pct = Math.round((progress.loaded / progress.total) * 100);
            setLoadProgress(pct);
            setLoadStatus(`Pobieranie: ${pct}%`);
            if (pct > lastProgress) {
              lastProgress = pct;
              lastProgressTime = Date.now();
            }
          }
        },
        (error) => {
          clearInterval(progressCheck);
          let msg = error.message || String(error);
          if (msg.includes("404")) msg = t.fileNotFound;
          else if (msg.includes("CORS")) msg = t.corsError;
          logError("Load error:", error);
          reject(new Error(msg));
        }
      );
    });
  }, [glbUrl]);

  // Initialize Three.js scene
  const initScene = useCallback((container) => {
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.01, 20);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.xr.enabled = true;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    renderer.domElement.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      logError("WebGL context lost");
      setLoadError(t.webglLost);
      setPhase("error");
    });

    // ============================================
    // [DEVICE]/[WEBGL] diagnostics - compare Samsung vs Motorola
    // ============================================
    try {
      console.info("[DEVICE] userAgent=%s platform=%s devicePixelRatio=%s",
        navigator.userAgent, navigator.platform, window.devicePixelRatio);
      const gl = renderer.getContext();
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      const glVendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
      const glRenderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
      const vsHigh = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT);
      const fsHigh = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
      const caps = renderer.capabilities;
      console.info("[WEBGL] version=%s vendor=%s renderer=%s",
        gl.getParameter(gl.VERSION), glVendor, glRenderer);
      console.info("[WEBGL] maxTextureSize=%d maxVertexUniforms=%d maxFragmentUniforms=%d maxVaryings=%d",
        gl.getParameter(gl.MAX_TEXTURE_SIZE),
        gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
        gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
        gl.getParameter(gl.MAX_VARYING_VECTORS));
      console.info("[WEBGL] isWebGL2=%s floatVertexTextures=%s maxVertexTextures=%d vertexHighpPrecision=%d maxAttributes=%d",
        caps.isWebGL2, caps.floatVertexTextures, gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
        vsHigh ? vsHigh.precision : -1, gl.getParameter(gl.MAX_VERTEX_ATTRIBS));
      // Surface GPU shader compile/link errors (invisible model symptom)
      renderer.debug.checkShaderErrors = true;
      renderer.debug.onShaderError = (glCtx, program, vs, fs) => {
        console.error("[WEBGL][SHADER_ERROR] vertex:", glCtx.getShaderInfoLog(vs));
        console.error("[WEBGL][SHADER_ERROR] fragment:", glCtx.getShaderInfoLog(fs));
        console.error("[WEBGL][SHADER_ERROR] program:", glCtx.getProgramInfoLog(program));
      };
    } catch (diagErr) {
      console.warn("[WEBGL] diagnostics error:", diagErr);
    }
    // ============================================

    // Lighting
    scene.add(new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1.2));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(1, 2, 1);
    scene.add(dirLight);

    // Reticle
    const reticle = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.12, 0.14, 32).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    reticle.add(ring);
    const dot = new THREE.Mesh(
      new THREE.CircleGeometry(0.02, 16).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    reticle.add(dot);
    reticle.matrixAutoUpdate = false;
    reticle.visible = false;
    scene.add(reticle);
    reticleRef.current = reticle;

    // Controller for tap
    const controller = renderer.xr.getController(0);
    controller.addEventListener("select", handleSelect);
    scene.add(controller);

    return { scene, camera, renderer };
  }, []);

  // Handle tap - place model (ONLY via explicit user tap)
  const handleSelect = useCallback(() => {
    const state = stateRef.current;
    const touch = touchRef.current;
    
    // Don't place if gesturing with 2 fingers
    if (touch.isGesturing || touch.count >= 2) {
      log("Select ignored - gesturing");
      return;
    }
    
    // CRITICAL: Only place if reticle is visible AND model is not already placed
    if (!state.reticleVisible) {
      log("Select ignored - reticle not visible");
      return;
    }
    if (state.modelPlaced) {
      log("Select ignored - model already placed");
      return;
    }
    
    // Check if hit-test is stable enough for placement
    const stability = hitStabilityRef.current;
    if (!stability.isStable) {
      log("Select ignored - hit not stable yet");
      // Could show a hint here
      return;
    }

    const reticle = reticleRef.current;
    const scene = sceneRef.current;
    const template = modelTemplateRef.current;

    if (!template || !reticle || !scene) {
      log("Select ignored - missing refs");
      return;
    }
    
    // Use stable hit position instead of raw reticle position
    const stableHit = stableHitRef.current;
    if (!stableHit) {
      log("Select ignored - no stable hit");
      return;
    }

    log("Placing model via user tap (stable hit)...");

    // IMPORTANT: Object3D.clone() does NOT correctly clone SkinnedMesh skeletons
    // (the cloned mesh keeps references to the ORIGINAL bones). When an
    // AnimationMixer then drives the clone's bones, the skinning breaks and on
    // some older GPUs/drivers the vertices collapse -> model invisible (while
    // newer GPUs may still render it). SkeletonUtils.clone() rebinds correctly.
    let hasSkinned = false;
    template.traverse((o) => {
      if (o.isSkinnedMesh) hasSkinned = true;
    });
    const placed = hasSkinned ? SkeletonUtils.clone(template) : template.clone();
    console.info(
      `[WEBXR][CLONE] skinned=${hasSkinned} method=${hasSkinned ? "SkeletonUtils.clone" : "Object3D.clone"}`
    );
    
    // IMPORTANT: the template already carries alignment offsets so the model is
    // centered horizontally and its BOTTOM sits at its own origin (y=0):
    //   template.position = (-center.x, -box.min.y, -center.z)
    // We must NOT overwrite the clone's position (that discards the alignment
    // and makes the model float/sink and sit off-center from the reticle).
    // Instead wrap the clone in a group and position the GROUP at the hit, so
    // the model's bottom-center lands exactly on the reticle point.
    const placedGroup = new THREE.Group();
    placedGroup.add(placed);
    placedGroup.position.set(stableHit.x, stableHit.y, stableHit.z);
    placedGroup.rotation.y = state.modelRotation;
    placedGroup.scale.copy(state.baseModelScale); // (1,1,1)

    lockedYRef.current = stableHit.y;
    console.info(
      `[PLACEMENT] reticle@tap=(${stableHit.x.toFixed(3)}, ${stableHit.y.toFixed(3)}, ${stableHit.z.toFixed(3)}) ` +
      `group=(${placedGroup.position.x.toFixed(3)}, ${placedGroup.position.y.toFixed(3)}, ${placedGroup.position.z.toFixed(3)}) ` +
      `innerModelOffset=(${placed.position.x.toFixed(3)}, ${placed.position.y.toFixed(3)}, ${placed.position.z.toFixed(3)})`
    );

    scene.add(placedGroup);
    placedModelRef.current = placedGroup;

    // [WEBXR][DIAG] Optional test cube to isolate GLB vs pipeline issues.
    // ?testcube=1 -> if the red cube is visible but the model is not, the WebXR
    // camera/renderer/placement pipeline is fine and the problem is the GLB
    // (materials/skinning/shaders). If the cube is also invisible, the issue is
    // lower (renderer/referenceSpace/camera).
    if (new URLSearchParams(window.location.search).get("testcube") === "1") {
      const cube = new THREE.Mesh(
        new THREE.BoxGeometry(0.2, 0.2, 0.2),
        new THREE.MeshBasicMaterial({ color: 0xff0000 })
      );
      cube.position.set(stableHit.x, stableHit.y + 0.1, stableHit.z);
      scene.add(cube);
      console.info("[WEBXR][DIAG] testcube=1 - red 20cm cube added at placement position");
    }
    
    // ============================================
    // ANIMATION SETUP - Create mixer and start animations
    // ============================================
    const clips = animationClipsRef.current;
    const noAnim = new URLSearchParams(window.location.search).get("noanim") === "1";
    if (noAnim) {
      console.info("[WEBXR][DIAG] noanim=1 - skipping AnimationMixer (diagnostic A/B)");
    }
    if (clips && clips.length > 0 && !noAnim) {
      log("🎬 [ANIMATION] Setting up animations for placed model...");
      
      // Create AnimationMixer for the placed model
      const mixer = new THREE.AnimationMixer(placed);
      animationMixerRef.current = mixer;
      
      // Clear previous actions
      animationActionsRef.current = [];
      
      // Start all animation clips
      clips.forEach((clip, index) => {
        const action = mixer.clipAction(clip);
        action.setLoop(THREE.LoopRepeat, Infinity); // Loop forever
        action.clampWhenFinished = false;
        action.play();
        animationActionsRef.current.push(action);
        log(`  🎬 Started animation "${clip.name}" (${clip.duration.toFixed(2)}s)`);
      });
      
      // Initialize animation time tracking
      lastAnimationTimeRef.current = performance.now();
      
      log("🎬 [ANIMATION] All animations started:", animationActionsRef.current.length, "clips playing");
    } else {
      log("🎬 [ANIMATION] No animations to play (static model)");
      animationMixerRef.current = null;
    }
    // ============================================
    
    reticle.visible = false;
    state.modelPlaced = true;
    state.reticleVisible = false;
    state.modelScale = 1.0;
    // Placement is the start of the AR experience: start audio from the beginning
    startAudio();
    setModelPlaced(true);
    setShowPlacedHint(true);
    setCurrentScalePercent(100);
    
    if (placedHintTimerRef.current) clearTimeout(placedHintTimerRef.current);
    placedHintTimerRef.current = setTimeout(() => setShowPlacedHint(false), 2000);
    log("Model placed at stable position:", [stableHit.x, stableHit.y, stableHit.z]);
  }, []);

  // Apply scale to placed model (respecting limits + snap to 100%)
  const applyScale = useCallback((newScale, isTemporary = false) => {
    const model = placedModelRef.current;
    const state = stateRef.current;
    if (!model) return;

    // Clamp scale
    let clampedScale = Math.max(SCALE_MIN, Math.min(SCALE_MAX, newScale));
    
    // Snap to 100% if close (within ±3%)
    if (Math.abs(clampedScale - 1.0) <= SNAP_THRESHOLD) {
      clampedScale = 1.0;
    }
    
    // Apply scale using base scale
    const finalScale = state.baseModelScale.clone().multiplyScalar(clampedScale);
    model.scale.copy(finalScale);
    
    // Update state only if not temporary (for snap-back)
    if (!isTemporary) {
      state.modelScale = clampedScale;
    }
    
    // Update UI - always show during scaling
    setCurrentScalePercent(Math.round(clampedScale * 100));
    setShowScaleIndicator(true);
    
    // Don't auto-hide the indicator - keep it visible while scaling
    // It will be hidden when gesture ends (in handleTouchEnd)
  }, []);

  // WebXR render loop
  const onXRFrame = useCallback((timestamp, frame) => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const reticle = reticleRef.current;
    const viewerHitTestSource = viewerHitTestSourceRef.current;
    const localSpace = localSpaceRef.current;
    const state = stateRef.current;
    const touch = touchRef.current;

    if (!frame || !renderer || !scene || !camera || !reticle) return;

    // Hit test for reticle (before placement) - with improved Y stabilization
    if (viewerHitTestSource && localSpace && !state.modelPlaced) {
      const results = frame.getHitTestResults(viewerHitTestSource);
      if (results.length > 0) {
        const pose = results[0].getPose(localSpace);
        if (pose) {
          const matrix = new THREE.Matrix4().fromArray(pose.transform.matrix);
          const hitPos = new THREE.Vector3().setFromMatrixPosition(matrix);
          const now = Date.now();

          // [XR HIT] throttled diagnostics (debug only)
          if (debugMode && now - (diagLogTsRef.current || 0) > 1000) {
            diagLogTsRef.current = now;
            const r = reticleRef.current;
            console.info(
              `[XR HIT] pos=(${hitPos.x.toFixed(3)}, ${hitPos.y.toFixed(3)}, ${hitPos.z.toFixed(3)}) | ` +
              `[RETICLE] pos=(${r ? r.position.x.toFixed(3) : "-"}, ${r ? r.position.y.toFixed(3) : "-"}, ${r ? r.position.z.toFixed(3) : "-"}) visible=${r ? r.visible : "-"}`
            );
          }
          
          // Add to hit history with timestamp
          hitHistoryRef.current.push({ 
            x: hitPos.x, 
            y: hitPos.y, 
            z: hitPos.z, 
            timestamp: now 
          });
          
          // Keep only recent hits (last HIT_HISTORY_SIZE)
          if (hitHistoryRef.current.length > HIT_HISTORY_SIZE) {
            hitHistoryRef.current.shift();
          }
          
          // Need minimum history for stabilization
          if (hitHistoryRef.current.length >= 8) {
            const history = hitHistoryRef.current;
            
            // Sort Y values and apply outlier rejection (trim top/bottom 20%)
            const yValues = history.map(h => h.y).sort((a, b) => a - b);
            const trimCount = Math.floor(yValues.length * OUTLIER_REJECTION);
            const trimmedY = yValues.slice(trimCount, yValues.length - trimCount);
            
            // Calculate robust median Y (from trimmed values)
            const medianY = trimmedY[Math.floor(trimmedY.length / 2)];
            
            // Calculate median X and Z
            const xValues = history.map(h => h.x).sort((a, b) => a - b);
            const zValues = history.map(h => h.z).sort((a, b) => a - b);
            const medianX = xValues[Math.floor(xValues.length / 2)];
            const medianZ = zValues[Math.floor(zValues.length / 2)];
            
            // Check if current hit Y is within threshold of median (not an outlier)
            const deltaY = Math.abs(hitPos.y - medianY);
            const isValidY = deltaY < HIT_Y_THRESHOLD;
            
            // Check XZ stability (should not jump around too much)
            const deltaXZ = Math.sqrt(
              Math.pow(hitPos.x - medianX, 2) + 
              Math.pow(hitPos.z - medianZ, 2)
            );
            const isValidXZ = deltaXZ < HIT_XZ_THRESHOLD;
            
            const isValidHit = isValidY && isValidXZ;
            
            // Check if hits have been stable for STABILITY_TIME_MS
            const stability = hitStabilityRef.current;
            if (isValidHit) {
              if (!stability.stableSince) {
                stability.stableSince = now;
              }
              const stableDuration = now - stability.stableSince;
              stability.isStable = stableDuration >= STABILITY_TIME_MS;
            } else {
              // Reset stability if hit is invalid
              stability.stableSince = null;
              stability.isStable = false;
            }
            
            // Update stable hit reference
            if (isValidHit) {
              stableHitRef.current = { x: medianX, y: medianY, z: medianZ };
            }
            
            // Update debug info (throttled to every 10 frames)
            if (history.length % 10 === 0) {
              setHitDebugInfo({
                hitY: hitPos.y.toFixed(3),
                medianY: medianY.toFixed(3),
                deltaY: deltaY.toFixed(3),
                deltaXZ: deltaXZ.toFixed(3),
                isValidHit,
                isStable: stability.isStable,
                historySize: history.length
              });
            }
            
            // Update reticle only if hit is valid
            if (isValidHit && stableHitRef.current) {
              // Use stabilized position for reticle
              const stablePos = stableHitRef.current;
              reticle.position.set(stablePos.x, stablePos.y, stablePos.z);
              
              // Preserve rotation from hit-test (floor orientation)
              const rotation = new THREE.Quaternion().setFromRotationMatrix(matrix);
              reticle.quaternion.copy(rotation);
              reticle.visible = true;
              state.reticleVisible = true;
              
              // Update stability state for UI
              setHitStable(stability.isStable);
              
              if (!state.hasHit) {
                state.hasHit = true;
                setHasHit(true);
                setNoHitWarning(false);
                if (noHitTimerRef.current) {
                  clearTimeout(noHitTimerRef.current);
                  noHitTimerRef.current = null;
                }
              }
            } else {
              // Invalid hit - don't update reticle, show as unstable
              setHitStable(false);
            }
          } else {
            // Not enough history - show reticle at raw position but mark as unstable
            reticle.visible = true;
            reticle.position.copy(hitPos);
            const rotation = new THREE.Quaternion().setFromRotationMatrix(matrix);
            reticle.quaternion.copy(rotation);
            state.reticleVisible = true;
            setHitStable(false);
          }
        }
      } else {
        reticle.visible = false;
        state.reticleVisible = false;
        setHitStable(false);
        if (state.hasHit) {
          state.hasHit = false;
          setHasHit(false);
        }
        if (!noHitTimerRef.current) {
          noHitTimerRef.current = setTimeout(() => {
            setNoHitWarning(true);
            noHitTimerRef.current = null;
          }, 4000);
        }
      }
    }

    // Drag with 1 finger - but ONLY when a drag was armed by touching the model
    // (dragActiveRef). Follows the CURRENT surface in X/Z AND Y, so the model
    // snaps to the height of whatever surface it is moved onto (floor -> step ->
    // table). Empty taps/drags never move the model because they don't arm it.
    if (state.modelPlaced && placedModelRef.current && !touch.isGesturing && dragActiveRef.current && transientHitTestSourceRef.current) {
      try {
        const transientResults = frame.getHitTestResultsForTransientInput(transientHitTestSourceRef.current);
        // Only move if exactly 1 touch point
        if (transientResults && transientResults.length === 1) {
          const results = transientResults[0].results;
          if (results && results.length > 0) {
            const pose = results[0].getPose(localSpace);
            if (pose) {
              const matrix = new THREE.Matrix4().fromArray(pose.transform.matrix);
              const newPos = new THREE.Vector3().setFromMatrixPosition(matrix);

              // Follow the currently detected surface (X/Z AND Y)
              placedModelRef.current.position.x = newPos.x;
              placedModelRef.current.position.y = newPos.y;
              placedModelRef.current.position.z = newPos.z;
              lockedYRef.current = newPos.y;

              // Explicitly preserve scale and rotation
              placedModelRef.current.rotation.y = state.modelRotation;
              const currentScale = state.baseModelScale.clone().multiplyScalar(state.modelScale);
              placedModelRef.current.scale.copy(currentScale);
            }
          }
        }
      } catch (e) {
        // Ignore transient input errors
      }
    }

    // ============================================
    // ANIMATION UPDATE - Update mixer every frame
    // ============================================
    if (animationMixerRef.current) {
      const now = performance.now();
      const deltaTime = (now - lastAnimationTimeRef.current) / 1000; // Convert to seconds
      lastAnimationTimeRef.current = now;
      
      // Update the animation mixer
      animationMixerRef.current.update(deltaTime);
    }
    // ============================================

    renderer.render(scene, camera);

    // ============================================
    // AR PHOTO CAPTURE (WebXR raw camera access)
    // Composites the real camera frame + the rendered model into one image.
    // Runs inside the XR frame because the camera texture is only valid now.
    // ============================================
    if (captureRequestedRef.current) {
      captureRequestedRef.current = false;
      try {
        const gl = renderer.getContext();
        const camPose = frame.getViewerPose(localSpace);
        const camView = camPose && camPose.views.find((v) => v.camera);
        const camTex = camView ? renderer.xr.getCameraTexture(camView.camera) : null;

        if (!camTex) {
          logError("Photo capture: no camera texture (camera-access unavailable)");
        } else {
          const W = gl.drawingBufferWidth;
          const H = gl.drawingBufferHeight;

          // Lazy composite resources (recreate if drawing buffer size changed)
          if (!captureRtRef.current || captureRtRef.current.width !== W || captureRtRef.current.height !== H) {
            if (captureRtRef.current) captureRtRef.current.dispose();
            captureRtRef.current = new THREE.WebGLRenderTarget(W, H);
            const bgScene = new THREE.Scene();
            const bgCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
            const quad = new THREE.Mesh(
              new THREE.PlaneGeometry(2, 2),
              new THREE.MeshBasicMaterial({ depthTest: false, depthWrite: false })
            );
            bgScene.add(quad);
            captureBgRef.current = { bgScene, bgCam, quad };
            captureCamRef.current = new THREE.PerspectiveCamera();
          }

          const { bgScene, bgCam, quad } = captureBgRef.current;
          quad.material.map = camTex;
          quad.material.needsUpdate = true;

          // Capture camera must match the current XR view (projection + pose)
          const xrArr = renderer.xr.getCamera();
          const sub = xrArr.cameras && xrArr.cameras.length ? xrArr.cameras[0] : xrArr;
          const capCam = captureCamRef.current;
          capCam.matrixAutoUpdate = false;
          capCam.matrixWorldAutoUpdate = false;
          capCam.projectionMatrix.copy(sub.projectionMatrix);
          capCam.projectionMatrixInverse.copy(sub.projectionMatrix).invert();
          capCam.matrixWorld.copy(sub.matrixWorld);
          capCam.matrixWorldInverse.copy(sub.matrixWorld).invert();

          const reticleWasVisible = reticle.visible;
          reticle.visible = false;

          const rt = captureRtRef.current;
          const xrWasEnabled = renderer.xr.enabled;
          const prevAutoClear = renderer.autoClear;
          renderer.xr.enabled = false;
          renderer.autoClear = false;
          renderer.setRenderTarget(rt);
          renderer.clear(true, true, true);
          renderer.render(bgScene, bgCam);   // real camera frame (background)
          renderer.render(scene, capCam);    // 3D model over the camera
          renderer.setRenderTarget(null);
          renderer.xr.enabled = xrWasEnabled;
          renderer.autoClear = prevAutoClear;
          reticle.visible = reticleWasVisible;

          // Read pixels and flip vertically (GL origin is bottom-left)
          const pixels = new Uint8Array(W * H * 4);
          renderer.readRenderTargetPixels(rt, 0, 0, W, H, pixels);
          const cv = document.createElement("canvas");
          cv.width = W;
          cv.height = H;
          const ctx = cv.getContext("2d");
          const imgData = ctx.createImageData(W, H);
          const rowBytes = W * 4;
          for (let y = 0; y < H; y++) {
            const srcStart = y * rowBytes;
            const dstStart = (H - 1 - y) * rowBytes;
            imgData.data.set(pixels.subarray(srcStart, srcStart + rowBytes), dstStart);
          }
          ctx.putImageData(imgData, 0, 0);
          cv.toBlob((blob) => {
            if (!blob) { logError("Photo capture: toBlob returned null"); return; }
            if (photoBlobRef.current?.url) URL.revokeObjectURL(photoBlobRef.current.url);
            const url = URL.createObjectURL(blob);
            photoBlobRef.current = { blob, url };
            setPhotoUrl(url);
          }, "image/png");
        }
      } catch (capErr) {
        logError("AR photo capture error:", capErr);
      }
    }
  }, []);

  // Calculate pinch distance
  const getPinchDistance = (touches) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate rotation angle
  const getRotationAngle = (touches) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.atan2(dy, dx);
  };

  // Raycast a screen point against the placed model (recursive through all
  // children: Group / SkinnedMesh / Mesh). Returns true if the point hits it.
  const raycastHitsModel = (clientX, clientY) => {
    const model = placedModelRef.current;
    const renderer = rendererRef.current;
    if (!model || !renderer) return false;
    const xrCam = renderer.xr.getCamera();
    const cam = xrCam.cameras && xrCam.cameras.length ? xrCam.cameras[0] : xrCam;
    const ndc = pointerNdcRef.current;
    ndc.x = (clientX / window.innerWidth) * 2 - 1;
    ndc.y = -(clientY / window.innerHeight) * 2 + 1;
    const ray = raycasterRef.current;
    ray.setFromCamera(ndc, cam);
    return ray.intersectObject(model, true).length > 0;
  };

  // Touch handlers (post-placement): 1 finger = drag model ONLY when it started
  // on the model; 2 fingers = rotate + pinch-scale ONLY when gesture starts on
  // or near the model. Empty taps/drags do nothing.
  const handleTouchStart = useCallback((e) => {
    const touch = touchRef.current;
    const state = stateRef.current;
    touch.count = e.touches.length;

    if (!state.modelPlaced) return;

    if (e.touches.length === 1) {
      const tch = e.touches[0];
      // Arm a drag ONLY if the finger landed on the model
      pendingDragRef.current = raycastHitsModel(tch.clientX, tch.clientY)
        ? { startX: tch.clientX, startY: tch.clientY }
        : null;
      dragActiveRef.current = false;
    } else if (e.touches.length === 2) {
      const a = e.touches[0];
      const b = e.touches[1];
      const midX = (a.clientX + b.clientX) / 2;
      const midY = (a.clientY + b.clientY) / 2;
      const onModel =
        raycastHitsModel(midX, midY) ||
        raycastHitsModel(a.clientX, a.clientY) ||
        raycastHitsModel(b.clientX, b.clientY);
      // Cancel any pending 1-finger drag when a second finger lands
      pendingDragRef.current = null;
      dragActiveRef.current = false;
      if (onModel) {
        touch.lastAngle = getRotationAngle(e.touches);
        touch.lastPinchDistance = getPinchDistance(e.touches);
        touch.isGesturing = true;
        touch.gestureType = "both";
        log("Gesture start (on model) - rotate + pinch");
      }
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = touchRef.current;
    const state = stateRef.current;
    touch.count = e.touches.length;

    if (!state.modelPlaced) return;

    // 1-finger drag: activate only after crossing the movement threshold.
    // The actual model movement happens in the XR frame loop (surface follow).
    if (e.touches.length === 1 && !touch.isGesturing && pendingDragRef.current) {
      if (!dragActiveRef.current) {
        const tch = e.touches[0];
        const dx = tch.clientX - pendingDragRef.current.startX;
        const dy = tch.clientY - pendingDragRef.current.startY;
        if (Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD_PX) {
          dragActiveRef.current = true;
          log("Drag activated (finger on model)");
        }
      }
      return;
    }

    // 2-finger rotate + pinch-scale
    if (e.touches.length === 2 && touch.isGesturing) {
      const model = placedModelRef.current;
      if (!model) return;

      // Rotation
      const currentAngle = getRotationAngle(e.touches);
      if (touch.lastAngle !== null && currentAngle !== null) {
        const delta = currentAngle - touch.lastAngle;
        state.modelRotation += delta;
        model.rotation.y = state.modelRotation;
      }
      touch.lastAngle = currentAngle;

      // Pinch scaling
      const currentDistance = getPinchDistance(e.touches);
      if (touch.lastPinchDistance !== null && currentDistance !== null && touch.lastPinchDistance > 0) {
        const scaleRatio = currentDistance / touch.lastPinchDistance;
        let newScale = state.modelScale * scaleRatio;

        // Clamp
        newScale = Math.max(SCALE_MIN, Math.min(SCALE_MAX, newScale));

        // Snap to 100% if close
        if (Math.abs(newScale - 1.0) <= SNAP_THRESHOLD) {
          newScale = 1.0;
        }

        // Apply scale (temporary if allowUserScaling is false)
        applyScale(newScale, !allowUserScaling);

        // If scaling is allowed, persist the scale
        if (allowUserScaling) {
          state.modelScale = newScale;
        }
      }
      touch.lastPinchDistance = currentDistance;
    }
  }, [allowUserScaling, applyScale]);

  const handleTouchEnd = useCallback((e) => {
    const touch = touchRef.current;
    const state = stateRef.current;
    touch.count = e.touches.length;

    if (e.touches.length < 2) {
      // Gesture ended
      if (touch.isGesturing) {
        // If scaling not allowed, snap back to 100%
        if (!allowUserScaling) {
          log("Snap back to 100% (scaling disabled)");
          state.modelScale = 1.0;
          applyScale(1.0, false);
        }
        log("Gesture end - final scale:", Math.round(state.modelScale * 100) + "%");

        // Hide scale indicator after delay
        if (scaleIndicatorTimerRef.current) clearTimeout(scaleIndicatorTimerRef.current);
        scaleIndicatorTimerRef.current = setTimeout(() => setShowScaleIndicator(false), 2000);
      }

      touch.isGesturing = false;
      touch.lastAngle = null;
      touch.lastPinchDistance = null;
      touch.gestureType = null;
    }

    // End 1-finger drag when the last finger lifts (model stays where it is)
    if (e.touches.length === 0) {
      dragActiveRef.current = false;
      pendingDragRef.current = null;
    }
  }, [allowUserScaling, applyScale]);

  // Stop overlay UI touches/pointer events from reaching the WebXR gesture logic
  const stopUiEvent = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Start WebXR session - SESSION FIRST, MODEL LATER
  const startAR = useCallback(async () => {
    if (isStarting || sessionRef.current) {
      log("Session already starting or exists, ignoring");
      return;
    }

    try {
      setIsStarting(true);
      setPhase("ar");
      // Unlock audio within this user gesture (do not play yet)
      unlockAudio();
      setLoadError(null);
      setLoadProgress(0);
      setLoadStatus("Uruchamiam AR...");
      setIsLoading(true);
      setModelLoaded(false);

      stateRef.current = {
        hasHit: false,
        modelPlaced: false,
        reticleVisible: false,
        modelRotation: 0,
        modelScale: 1.0,
        baseModelScale: new THREE.Vector3(1, 1, 1),
      };
      touchRef.current = { count: 0, lastAngle: null, lastPinchDistance: null, isGesturing: false, gestureType: null };
      setHasHit(false);
      setModelPlaced(false);
      setNoHitWarning(false);
      setShowPlacedHint(false);
      setCurrentScalePercent(100);

      // 1. Check WebXR support - if not supported, auto-fallback to Scene Viewer
      const hasNavigatorXR = !!navigator.xr;
      let isSessionSupported = false;
      
      if (hasNavigatorXR) {
        try {
          isSessionSupported = await navigator.xr.isSessionSupported("immersive-ar");
        } catch (e) {
          log("isSessionSupported check failed:", e.message);
        }
      }
      
      console.info("[WEBXR_SUPPORT]", { hasNavigatorXR, isSessionSupported });
      
      if (!hasNavigatorXR || !isSessionSupported) {
        log("WebXR not supported - auto fallback to Scene Viewer");
        setIsStarting(false);
        onFallbackToSceneViewer?.();
        return;
      }

      // 2. Wait for container
      let container = null;
      for (let i = 0; i < 10; i++) {
        await new Promise(r => requestAnimationFrame(r));
        container = containerRef.current;
        if (container) break;
      }
      if (!container) throw new Error(t.containerUnavailable);

      // 3. Initialize scene (without model yet)
      initLoaders();
      initScene(container);

      const renderer = rendererRef.current;

      // 4. START AR SESSION IMMEDIATELY
      log("Starting AR session...");
      let session;
      try {
        session = await navigator.xr.requestSession("immersive-ar", {
          requiredFeatures: ["hit-test"],
          optionalFeatures: ["dom-overlay", "camera-access"],
          domOverlay: { root: document.getElementById("webxr-overlay") },
        });
      } catch (sessionErr) {
        logError("requestSession failed:", sessionErr.name, sessionErr.message);
        // Auto-fallback to Scene Viewer instead of showing error
        log("requestSession failed - auto fallback to Scene Viewer");
        setIsStarting(false);
        onFallbackToSceneViewer?.();
        return;
      }

      sessionRef.current = session;
      log("AR session started, setting up...");

      renderer.xr.setReferenceSpaceType("local");
      await renderer.xr.setSession(session);

      // Raw Camera Access availability (for in-AR photo). Only enable the camera
      // button if the UA actually granted the feature for this session.
      const camAccess = !!(session.enabledFeatures && session.enabledFeatures.includes("camera-access"));
      setCameraAccessAvailable(camAccess);
      console.info("[WEBXR] camera-access enabled:", camAccess);

      // Suppress WebXR "select" (placement) when the tap lands on an overlay
      // control (buttons / photo modal), so tapping the camera button etc. never
      // places or moves the model.
      const overlayEl = document.getElementById("webxr-overlay");
      if (overlayEl) {
        overlayEl.addEventListener("beforexrselect", (ev) => {
          if (ev.target && ev.target.closest && ev.target.closest("button, .webxr-photo-modal")) {
            ev.preventDefault();
          }
        });
      }

      const localSpace = await session.requestReferenceSpace("local");
      localSpaceRef.current = localSpace;
      console.info("[WEBXR] session started, referenceSpace=local");

      const viewerSpace = await session.requestReferenceSpace("viewer");
      viewerHitTestSourceRef.current = await session.requestHitTestSource({ space: viewerSpace });
      console.info("[WEBXR] hit-test source created (viewer space)");

      try {
        transientHitTestSourceRef.current = await session.requestHitTestSourceForTransientInput({
          profile: "generic-touchscreen",
        });
        log("Transient input enabled");
      } catch (e) {
        log("Transient input not available");
      }

      session.addEventListener("end", () => {
        log("Session ended by system");
        sessionRef.current = null;
        cleanup();
        onClose?.();
      });

      // Start render loop (shows camera feed + reticle)
      renderer.setAnimationLoop(onXRFrame);
      setIsStarting(false);
      log("AR session active, loading model...");

      // 5. LOAD MODEL AFTER SESSION IS RUNNING
      setLoadStatus("Ładowanie modelu...");
      try {
        await loadModel();
        log("Model loaded successfully");
        setIsLoading(false);
      } catch (modelErr) {
        logError("Model load failed:", modelErr);
        setLoadError(`${t.modelLoadFailedPrefix}: ${modelErr.message}`);
        setPhase("model-error");
        setIsLoading(false);
      }

    } catch (err) {
      logError("Unexpected error:", err);
      // Auto-fallback to Scene Viewer on any error
      log("Unexpected error - auto fallback to Scene Viewer");
      setIsStarting(false);
      onFallbackToSceneViewer?.();
    }
  }, [isStarting, initLoaders, loadModel, initScene, onXRFrame, onClose, onFallbackToSceneViewer]);

  // Cleanup function - MUST be defined before handleFallback
  const cleanup = useCallback(() => {
    log("Cleanup started");

    // Stop + reset audio immediately
    stopAudio();

    // Stop animations and cleanup mixer
    if (animationMixerRef.current) {
      log("🎬 [ANIMATION] Cleanup: stopping animations");
      animationMixerRef.current.stopAllAction();
      animationMixerRef.current = null;
    }
    animationActionsRef.current = [];
    animationClipsRef.current = [];
    
    if (noHitTimerRef.current) clearTimeout(noHitTimerRef.current);
    if (placedHintTimerRef.current) clearTimeout(placedHintTimerRef.current);
    if (scaleIndicatorTimerRef.current) clearTimeout(scaleIndicatorTimerRef.current);
    if (dracoLoaderRef.current) { dracoLoaderRef.current.dispose(); dracoLoaderRef.current = null; }
    if (rendererRef.current) {
      rendererRef.current.setAnimationLoop(null);
      if (rendererRef.current.domElement?.parentNode) {
        rendererRef.current.domElement.parentNode.removeChild(rendererRef.current.domElement);
      }
      rendererRef.current.dispose();
    }
    const session = sessionRef.current;
    if (session) {
      sessionRef.current = null;
      session.end().catch((e) => log("Session end error (ignored):", e.message));
    }
    rendererRef.current = null;
    sceneRef.current = null;
    cameraRef.current = null;
    reticleRef.current = null;
    modelTemplateRef.current = null;
    placedModelRef.current = null;
    viewerHitTestSourceRef.current = null;
    transientHitTestSourceRef.current = null;
    localSpaceRef.current = null;

    // Dispose AR photo capture resources
    if (captureRtRef.current) { captureRtRef.current.dispose(); captureRtRef.current = null; }
    if (captureBgRef.current) {
      captureBgRef.current.quad.geometry.dispose();
      captureBgRef.current.quad.material.dispose();
      captureBgRef.current = null;
    }
    captureCamRef.current = null;
    if (photoBlobRef.current?.url) URL.revokeObjectURL(photoBlobRef.current.url);
    photoBlobRef.current = null;

    log("Cleanup complete");
  }, []);

  // ===== AR Photo (camera-access) handlers =====
  const takePhoto = useCallback(() => {
    captureRequestedRef.current = true;
  }, []);

  const closePhoto = useCallback(() => {
    if (photoBlobRef.current?.url) URL.revokeObjectURL(photoBlobRef.current.url);
    photoBlobRef.current = null;
    setPhotoUrl(null);
  }, []);

  const photoFileName = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `ARLens_${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}.png`;
  };

  const savePhoto = useCallback(() => {
    const data = photoBlobRef.current;
    if (!data) return;
    const a = document.createElement("a");
    a.href = data.url;
    a.download = photoFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const sharePhoto = useCallback(async () => {
    const data = photoBlobRef.current;
    if (!data) return;
    try {
      const file = new File([data.blob], photoFileName(), { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        savePhoto();
      }
    } catch (shareErr) {
      log("Share cancelled/failed:", shareErr.message);
    }
  }, [savePhoto]);

  const handleFallback = useCallback(() => {
    cleanup();
    onFallbackToSceneViewer?.();
  }, [onFallbackToSceneViewer, cleanup]);

  // Retry model load
  const retryModelLoad = useCallback(async () => {
    if (phase !== "model-error") return;
    setPhase("ar");
    setIsLoading(true);
    setLoadError(null);
    setLoadStatus("Ponowne ładowanie...");
    try {
      await loadModel();
      log("Model loaded on retry");
      setIsLoading(false);
    } catch (err) {
      logError("Retry model load failed:", err);
      setLoadError(`${t.modelLoadFailedPrefix}: ${err.message}`);
      setPhase("model-error");
      setIsLoading(false);
    }
  }, [phase, loadModel]);

  useEffect(() => () => cleanup(), [cleanup]);

  const exitAR = () => { cleanup(); onClose?.(); };

  // Reset placement - allows user to reposition model
  const resetPlacement = useCallback(() => {
    const scene = sceneRef.current;
    const reticle = reticleRef.current;
    const placedModel = placedModelRef.current;
    const state = stateRef.current;

    log("Reset placement requested");

    // Stop audio and reset to start
    stopAudio();

    // Stop all animations and clear mixer
    if (animationMixerRef.current) {
      log("🎬 [ANIMATION] Stopping animations...");
      animationMixerRef.current.stopAllAction();
      animationMixerRef.current = null;
      animationActionsRef.current = [];
    }

    // Remove placed model from scene completely
    if (placedModel && scene) {
      scene.remove(placedModel);
      placedModelRef.current = null;
      log("Model removed from scene");
    }

    // Reset ALL placement-related state
    state.modelPlaced = false;
    state.modelScale = 1.0;
    state.reticleVisible = false; // Force reticle to re-acquire
    state.hasHit = false;
    
    // Reset hit-test stabilization
    lockedYRef.current = null;
    hitHistoryRef.current = [];
    stableHitRef.current = null;
    hitStabilityRef.current = { isStable: false, stableSince: null };

    // Reset UI state
    setModelPlaced(false);
    setHasHit(false); // Force UI to show "searching" state
    setHitStable(false);
    setCurrentScalePercent(100);
    setShowScaleIndicator(false);
    setShowPlacedHint(false);

    // Hide reticle until new hit is found
    if (reticle) {
      reticle.visible = false;
    }

    log("Placement reset complete - waiting for new tap");
  }, []);

  // ========== RENDER ==========

  if (phase === "error") {
    return (
      <div className="webxr-fullscreen">
        <div className="webxr-onboarding">
          <button className="webxr-close" onClick={onClose}><X size={24} /></button>
          <div className="webxr-error-icon"><AlertCircle size={48} /></div>
          <h2 className="webxr-title">{t.arLaunchFailedTitle}</h2>
          <p className="webxr-subtitle" style={{ maxWidth: 280, margin: "0 auto 32px" }}>
            {loadError || t.arLaunchFailedSub}
          </p>
          <div className="webxr-buttons">
            <Button onClick={handleFallback} className="webxr-start-btn">{t.runStandardAr}</Button>
            <button onClick={onClose} className="webxr-link">{t.backTo3d}</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "onboarding") {
    return (
      <div className="webxr-fullscreen">
        <div className="webxr-onboarding">
          <button className="webxr-close" onClick={onClose}><X size={24} /></button>
          <h2 className="webxr-title">{projectName || t.model3d}</h2>
          <p className="webxr-subtitle">{t.arInBrowser}</p>
          <div className="webxr-tips">
            <div className="webxr-tip"><Move size={20} /><span>{t.pointCameraFloor}</span></div>
            <div className="webxr-tip"><Hand size={20} /><span>{t.tapWhenGreen}</span></div>
          </div>
          <div className="webxr-buttons">
            <Button 
              onClick={startAR} 
              className="webxr-start-btn"
              disabled={isStarting}
            >
              {isStarting ? t.launching : t.launchAr}
            </Button>
            <button onClick={handleFallback} className="webxr-link">{t.useStandardArMode}</button>
          </div>
        </div>
      </div>
    );
  }

  // AR View (including model-error state)
  return (
    <div className="webxr-ar-container">
      <div ref={containerRef} className="webxr-canvas-container" />

      {/* Model error overlay */}
      {phase === "model-error" && (
        <div className="webxr-loading-overlay" style={{ background: "rgba(0,0,0,0.85)" }}>
          <div style={{ marginBottom: 16 }}><AlertCircle size={48} color="#ef4444" /></div>
          <p style={{ color: "#ef4444", fontWeight: 600, marginBottom: 8 }}>
            {t.modelLoadErrorTitle}
          </p>
          <p style={{ fontSize: 13, opacity: 0.8, marginBottom: 24, maxWidth: 280, color: "#fff" }}>
            {loadError || t.modelLoadErrorSub}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 220 }}>
            <Button onClick={retryModelLoad} className="webxr-start-btn" style={{ width: "100%" }}>
              {t.tryAgain}
            </Button>
            <button onClick={handleFallback} className="webxr-link" style={{ color: "#fff" }}>
              {t.useStandardAr}
            </button>
            <button onClick={exitAR} className="webxr-link" style={{ color: "#888" }}>
              {t.finish}
            </button>
          </div>
        </div>
      )}

      <div 
        id="webxr-overlay" 
        className="webxr-ui"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Scale indicator - must be inside DOM overlay to be visible in WebXR */}
        {modelPlaced && (
          <div className={`webxr-scale-indicator ${showScaleIndicator ? 'webxr-scale-active' : ''}`}>
            {currentScalePercent}%
          </div>
        )}

        {/* Debug panel - shows scale and model info */}
        {debugMode && (
          <div style={{
            position: "fixed",
            bottom: 80,
            left: 10,
            right: 10,
            background: "rgba(0,0,0,0.85)",
            color: "#0f0",
            padding: 10,
            borderRadius: 8,
            fontSize: 10,
            fontFamily: "monospace",
            zIndex: 9999,
            pointerEvents: "none"
          }}>
            <div style={{ color: "#fff", fontWeight: "bold", marginBottom: 4 }}>🔧 WebXR Debug</div>
            
            {/* Scale info */}
            <div style={{ borderBottom: "1px solid #333", paddingBottom: 4, marginBottom: 4 }}>
              <div><b>baseModelScale:</b> {stateRef.current.baseModelScale ? 
                `(${stateRef.current.baseModelScale.x.toFixed(2)}, ${stateRef.current.baseModelScale.y.toFixed(2)}, ${stateRef.current.baseModelScale.z.toFixed(2)})` : "?"}</div>
              <div><b>currentScale:</b> {stateRef.current.modelScale?.toFixed(2) || "?"}</div>
              <div><b>scalePercent:</b> {currentScalePercent}%</div>
              {stateRef.current.originalSize && (
                <div><b>originalSize:</b> {stateRef.current.originalSize.x.toFixed(2)}m × {stateRef.current.originalSize.y.toFixed(2)}m × {stateRef.current.originalSize.z.toFixed(2)}m</div>
              )}
              <div style={{ color: currentScalePercent === 100 ? "#0f0" : "#ff0" }}>
                <b>100% = scale 1.0:</b> {currentScalePercent === 100 && stateRef.current.modelScale === 1.0 ? "✅ TAK" : "⚠️ NIE"}
              </div>
            </div>
            
            {/* Hit-test info */}
            <div style={{ borderBottom: "1px solid #333", paddingBottom: 4, marginBottom: 4 }}>
              <div style={{ color: "#0ff" }}><b>Hit-Test:</b></div>
              <div><b>hitY:</b> {hitDebugInfo.hitY || "?"}</div>
              <div><b>medianY:</b> {hitDebugInfo.medianY || "?"}</div>
              <div><b>deltaY:</b> <span style={{ color: parseFloat(hitDebugInfo.deltaY) < 0.08 ? "#0f0" : "#f55" }}>{hitDebugInfo.deltaY || "?"}</span></div>
              <div><b>deltaXZ:</b> {hitDebugInfo.deltaXZ || "?"}</div>
              <div><b>historySize:</b> {hitDebugInfo.historySize || "0"}</div>
              <div><b>isValidHit:</b> {hitDebugInfo.isValidHit ? "✅" : "❌"}</div>
              <div><b>isStable:</b> <span style={{ color: hitDebugInfo.isStable ? "#0f0" : "#ff0" }}>{hitDebugInfo.isStable ? "✅ READY" : "⏳ stabilizing..."}</span></div>
            </div>
            
            {/* Locked Y */}
            <div>
              <div><b>lockedY:</b> {lockedYRef.current?.toFixed(3) || "(not placed)"}</div>
            </div>
          </div>
        )}

        <div className="webxr-top-buttons">
          <button className="webxr-exit-btn" onClick={exitAR} onPointerDown={stopUiEvent} onTouchStart={stopUiEvent}>
            <X size={20} /><span>{t.finish}</span>
          </button>
          {modelPlaced && (
            <button className="webxr-reset-btn" onClick={resetPlacement} onPointerDown={stopUiEvent} onTouchStart={stopUiEvent}>
              <RotateCcw size={16} /><span>{t.placeAgain}</span>
            </button>
          )}
        </div>

        {/* AR photo button - only when camera-access is actually available */}
        {cameraAccessAvailable && modelLoaded && phase !== "model-error" && !photoUrl && (
          <button
            className="webxr-photo-btn"
            onClick={takePhoto}
            onPointerDown={stopUiEvent}
            onTouchStart={stopUiEvent}
            data-testid="webxr-photo-btn"
            aria-label={t.arTakePhoto}
          >
            <Camera size={26} />
          </button>
        )}

        {/* AR photo preview modal */}
        {photoUrl && (
          <div className="webxr-photo-modal" data-testid="webxr-photo-modal" onPointerDown={stopUiEvent} onTouchStart={stopUiEvent}>
            <div className="webxr-photo-card">
              <img src={photoUrl} alt={t.arPhotoTitle} className="webxr-photo-img" />
              <div className="webxr-photo-actions">
                <button className="webxr-photo-save" onClick={savePhoto} onPointerDown={stopUiEvent} onTouchStart={stopUiEvent} data-testid="webxr-photo-save">
                  <Download size={18} /><span>{t.arPhotoSave}</span>
                </button>
                {typeof navigator !== "undefined" && typeof navigator.canShare === "function" && (
                  <button className="webxr-photo-share" onClick={sharePhoto} onPointerDown={stopUiEvent} onTouchStart={stopUiEvent} data-testid="webxr-photo-share">
                    <Share2 size={18} /><span>{t.arPhotoShare}</span>
                  </button>
                )}
                <button className="webxr-photo-close" onClick={closePhoto} onPointerDown={stopUiEvent} onTouchStart={stopUiEvent} data-testid="webxr-photo-close">
                  {t.arPhotoClose}
                </button>
              </div>
            </div>
          </div>
        )}

        {phase !== "model-error" && (
          <div className="webxr-hint-area">
            {/* Model loading hint - shown while model is being loaded */}
            {!modelLoaded && (
              <div className="webxr-hint webxr-hint-loading">
                <Loader2 size={20} className="animate-spin" />
                <p>{t.loadingModel}</p>
                {loadProgress > 0 && loadProgress < 100 && (
                  <span className="webxr-hint-progress">{loadProgress}%</span>
                )}
              </div>
            )}
            {/* Searching for surface */}
            {modelLoaded && !modelPlaced && !hasHit && !noHitWarning && (
              <div className="webxr-hint">
                <div className="webxr-hint-spinner" />
                <p>{t.searchingSurface}</p>
              </div>
            )}
            {/* No hit warning */}
            {modelLoaded && !modelPlaced && !hasHit && noHitWarning && (
              <div className="webxr-hint webxr-hint-warning">
                <p>{t.cantDetectFloor}</p>
                <span>{t.tryBetterLight}</span>
              </div>
            )}
            {/* Stabilizing - hit found but not stable yet */}
            {modelLoaded && !modelPlaced && hasHit && !hitStable && (
              <div className="webxr-hint">
                <div className="webxr-hint-spinner" />
                <p>{t.stabilizing}</p>
              </div>
            )}
            {/* Ready to place - hit is stable */}
            {modelLoaded && !modelPlaced && hasHit && hitStable && (
              <div className="webxr-hint webxr-hint-ready">
                <p>{t.tapToPlace}</p>
              </div>
            )}
            {/* Just placed */}
            {modelPlaced && showPlacedHint && (
              <div className="webxr-hint webxr-hint-placed">
                <p>{t.modelPlaced}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebXRViewer;
